import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Video, VideoOff, Mic, MicOff, Monitor, Phone, PhoneOff,
  Users, Calendar, Clock, Plus, Play, Pause, CheckCircle2,
  Circle, FileText, MessageSquare, ClipboardList, Settings,
  ChevronRight, ChevronDown, X, Send, Link, Trash2, Edit,
  AlertTriangle, Shield, GraduationCap, Wrench, ExternalLink,
  Copy, Check, Loader2, RefreshCw, UserPlus, File
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Meeting type configuration
const MEETING_TYPE_CONFIG = {
  toolbox_talk: { label: 'Toolbox Talk', icon: Wrench, color: 'bg-blue-500' },
  safety_standown: { label: 'Safety Stand-Down', icon: Shield, color: 'bg-red-500' },
  incident_review: { label: 'Incident Review', icon: AlertTriangle, color: 'bg-orange-500' },
  training: { label: 'Training Session', icon: GraduationCap, color: 'bg-green-500' },
  general: { label: 'General Meeting', icon: Users, color: 'bg-purple-500' }
};

// Status configuration
const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const SafetyMeetingsPage = () => {
  const { toast } = useToast();
  
  // State
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [templates, setTemplates] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('meetings');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showMeetingRoom, setShowMeetingRoom] = useState(false);
  const [showAddAttendeeDialog, setShowAddAttendeeDialog] = useState(false);
  const [showAddDocDialog, setShowAddDocDialog] = useState(false);
  
  // Form state
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    meeting_type: 'toolbox_talk',
    description: '',
    scheduled_date: new Date().toISOString().split('T')[0],
    scheduled_time: '10:00',
    duration_minutes: 30,
    location: ''
  });
  
  // Chat state
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const chatEndRef = useRef(null);
  
  // New attendee state
  const [newAttendee, setNewAttendee] = useState({ name: '', email: '', role: '' });
  
  // New document state
  const [newDoc, setNewDoc] = useState({ name: '', url: '', type: 'link' });
  
  // Action item state
  const [newActionItem, setNewActionItem] = useState({ title: '', assignee: '', due_date: '', priority: 'medium' });

  // Fetch data on mount
  useEffect(() => {
    fetchMeetings();
    fetchTemplates();
    fetchStats();
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchMeetings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/video-meetings/meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeetings(response.data.meetings || []);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/video-meetings/templates`);
      setTemplates(response.data.templates || {});
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/video-meetings/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateMeeting = async () => {
    if (!newMeeting.title.trim()) {
      toast({ title: "Error", description: "Please enter a meeting title", variant: "destructive" });
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/video-meetings/meetings`, newMeeting, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Meeting created successfully" });
      setShowCreateDialog(false);
      setNewMeeting({
        title: '',
        meeting_type: 'toolbox_talk',
        description: '',
        scheduled_date: new Date().toISOString().split('T')[0],
        scheduled_time: '10:00',
        duration_minutes: 30,
        location: ''
      });
      fetchMeetings();
      fetchStats();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create meeting", variant: "destructive" });
    }
  };

  const handleCreateFromTemplate = async (templateType) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_URL}/api/video-meetings/meetings/from-template/${templateType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ title: "Success", description: "Meeting created from template" });
      fetchMeetings();
      fetchStats();
      setSelectedMeeting(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to create meeting", variant: "destructive" });
    }
  };

  const handleStartMeeting = async (meeting) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/video-meetings/meetings/${meeting.id}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedMeeting({ ...meeting, status: 'in_progress' });
      setShowMeetingRoom(true);
      setChatMessages(meeting.chat_messages || []);
      fetchMeetings();
    } catch (error) {
      toast({ title: "Error", description: "Failed to start meeting", variant: "destructive" });
    }
  };

  const handleEndMeeting = async () => {
    if (!selectedMeeting) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ title: "Meeting Ended", description: "Meeting has been completed" });
      setShowMeetingRoom(false);
      setSelectedMeeting(null);
      fetchMeetings();
      fetchStats();
    } catch (error) {
      toast({ title: "Error", description: "Failed to end meeting", variant: "destructive" });
    }
  };

  const handleSendChat = async () => {
    if (!chatMessage.trim() || !selectedMeeting) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const userName = localStorage.getItem('user_name') || 'Anonymous';
      
      const response = await axios.post(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}/chat`,
        { message: chatMessage, sender_name: userName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChatMessages([...chatMessages, response.data]);
      setChatMessage('');
    } catch (error) {
      console.error('Error sending chat:', error);
    }
  };

  const handleToggleAgendaItem = async (itemIndex) => {
    if (!selectedMeeting) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}/agenda/${itemIndex}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const updatedAgenda = [...selectedMeeting.agenda];
      updatedAgenda[itemIndex].completed = response.data.completed;
      setSelectedMeeting({ ...selectedMeeting, agenda: updatedAgenda });
    } catch (error) {
      console.error('Error toggling agenda item:', error);
    }
  };

  const handleAddAttendee = async () => {
    if (!newAttendee.name.trim() || !selectedMeeting) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}/attendees`,
        newAttendee,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ title: "Success", description: "Attendee added" });
      setNewAttendee({ name: '', email: '', role: '' });
      setShowAddAttendeeDialog(false);
      
      // Refresh meeting
      const response = await axios.get(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedMeeting(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to add attendee", variant: "destructive" });
    }
  };

  const handleAddDocument = async () => {
    if (!newDoc.name.trim() || !newDoc.url.trim() || !selectedMeeting) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}/documents`,
        newDoc,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ title: "Success", description: "Document added" });
      setNewDoc({ name: '', url: '', type: 'link' });
      setShowAddDocDialog(false);
      
      // Refresh meeting
      const response = await axios.get(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedMeeting(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to add document", variant: "destructive" });
    }
  };

  const handleAddActionItem = async () => {
    if (!newActionItem.title.trim() || !selectedMeeting) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}/action-items`,
        newActionItem,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ title: "Success", description: "Action item added" });
      setNewActionItem({ title: '', assignee: '', due_date: '', priority: 'medium' });
      
      // Refresh meeting
      const response = await axios.get(
        `${API_URL}/api/video-meetings/meetings/${selectedMeeting.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedMeeting(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to add action item", variant: "destructive" });
    }
  };

  const copyMeetingLink = (url) => {
    navigator.clipboard.writeText(url);
    toast({ title: "Copied!", description: "Meeting link copied to clipboard" });
  };

  // Meeting Card Component
  const MeetingCard = ({ meeting }) => {
    const TypeIcon = MEETING_TYPE_CONFIG[meeting.meeting_type]?.icon || Users;
    const typeConfig = MEETING_TYPE_CONFIG[meeting.meeting_type] || {};
    const statusConfig = STATUS_CONFIG[meeting.status] || {};
    
    return (
      <Card 
        className={`cursor-pointer transition-all hover:shadow-md ${
          selectedMeeting?.id === meeting.id ? 'ring-2 ring-blue-500' : ''
        }`}
        onClick={() => setSelectedMeeting(meeting)}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${typeConfig.color} text-white`}>
                <TypeIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{meeting.title}</h3>
                <p className="text-sm text-slate-500">{typeConfig.label}</p>
                <div className="flex items-center gap-3 mt-2 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(meeting.scheduled_date).toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {meeting.scheduled_time}
                  </span>
                </div>
              </div>
            </div>
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
          </div>
          
          {meeting.status === 'scheduled' && (
            <Button 
              className="w-full mt-3" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleStartMeeting(meeting);
              }}
            >
              <Play className="w-4 h-4 mr-2" />
              Start Meeting
            </Button>
          )}
          
          {meeting.status === 'in_progress' && (
            <Button 
              className="w-full mt-3 bg-green-600 hover:bg-green-700" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMeeting(meeting);
                setChatMessages(meeting.chat_messages || []);
                setShowMeetingRoom(true);
              }}
            >
              <Video className="w-4 h-4 mr-2" />
              Join Meeting
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  // Meeting Room Component (Jitsi Embed)
  const MeetingRoom = () => {
    if (!selectedMeeting) return null;
    
    const agendaProgress = selectedMeeting.agenda?.length > 0 
      ? Math.round((selectedMeeting.agenda.filter(i => i.completed).length / selectedMeeting.agenda.length) * 100)
      : 0;
    
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col" data-testid="meeting-room">
        {/* Header */}
        <div className="bg-slate-800 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Video className="w-6 h-6 text-green-400" />
            <div>
              <h2 className="text-white font-semibold">{selectedMeeting.title}</h2>
              <p className="text-slate-400 text-sm">{selectedMeeting.template_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              className="text-slate-300 border-slate-600"
              onClick={() => copyMeetingLink(selectedMeeting.jitsi_room_url)}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleEndMeeting}
            >
              <PhoneOff className="w-4 h-4 mr-2" />
              End Meeting
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-slate-400"
              onClick={() => setShowMeetingRoom(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Video Area */}
          <div className="flex-1 p-4">
            <div className="h-full bg-black rounded-lg overflow-hidden">
              <iframe
                src={`${selectedMeeting.jitsi_room_url}#config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false`}
                title="Safety Meeting"
                className="w-full h-full"
                allow="camera; microphone; fullscreen; display-capture; autoplay"
                allowFullScreen
              />
            </div>
          </div>
          
          {/* Side Panel */}
          <div className="w-96 bg-slate-800 flex flex-col">
            <Tabs defaultValue="agenda" className="flex-1 flex flex-col">
              <TabsList className="mx-4 mt-4">
                <TabsTrigger value="agenda" className="flex-1">Agenda</TabsTrigger>
                <TabsTrigger value="chat" className="flex-1">Chat</TabsTrigger>
                <TabsTrigger value="attendees" className="flex-1">Attendees</TabsTrigger>
              </TabsList>
              
              {/* Agenda Tab */}
              <TabsContent value="agenda" className="flex-1 flex flex-col m-0 p-4">
                <div className="mb-3">
                  <div className="flex items-center justify-between text-sm text-slate-400 mb-1">
                    <span>Progress</span>
                    <span>{agendaProgress}%</span>
                  </div>
                  <Progress value={agendaProgress} className="h-2" />
                </div>
                
                <ScrollArea className="flex-1">
                  <div className="space-y-2">
                    {selectedMeeting.agenda?.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleToggleAgendaItem(idx)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          item.completed 
                            ? 'bg-green-900/30 border border-green-700' 
                            : 'bg-slate-700 hover:bg-slate-600'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {item.completed ? (
                            <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                          ) : (
                            <Circle className="w-5 h-5 text-slate-400 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <div className={`font-medium ${item.completed ? 'text-green-400 line-through' : 'text-white'}`}>
                              {item.title}
                            </div>
                            <div className="text-sm text-slate-400">
                              {item.duration_minutes} min
                              {item.presenter && ` • ${item.presenter}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
              
              {/* Chat Tab */}
              <TabsContent value="chat" className="flex-1 flex flex-col m-0 p-4">
                <ScrollArea className="flex-1 mb-3">
                  <div className="space-y-3">
                    {chatMessages.map((msg, idx) => (
                      <div key={idx} className="bg-slate-700 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-blue-400">{msg.sender_name}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-200">{msg.message}</p>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>
                
                <div className="flex gap-2">
                  <Input
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                    placeholder="Type a message..."
                    className="flex-1 bg-slate-700 border-slate-600 text-white"
                  />
                  <Button onClick={handleSendChat} size="icon">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </TabsContent>
              
              {/* Attendees Tab */}
              <TabsContent value="attendees" className="flex-1 flex flex-col m-0 p-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mb-3 border-slate-600 text-slate-300"
                  onClick={() => setShowAddAttendeeDialog(true)}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Attendee
                </Button>
                
                <ScrollArea className="flex-1">
                  <div className="space-y-2">
                    {selectedMeeting.attendees?.map((attendee, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
                            {attendee.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-white text-sm font-medium">{attendee.name}</div>
                            {attendee.role && (
                              <div className="text-slate-400 text-xs">{attendee.role}</div>
                            )}
                          </div>
                        </div>
                        {attendee.attended && (
                          <Badge className="bg-green-600 text-white text-xs">Present</Badge>
                        )}
                      </div>
                    ))}
                    
                    {(!selectedMeeting.attendees || selectedMeeting.attendees.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        No attendees added yet
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="safety-meetings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Video className="w-7 h-7 text-blue-500" />
            Safety Meetings
          </h1>
          <p className="text-slate-500">Video conferencing and collaboration for safety discussions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchMeetings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Meeting
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-900">{stats.total_meetings}</div>
              <div className="text-sm text-slate-500">Total Meetings</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              <div className="text-sm text-slate-500">Completed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.scheduled}</div>
              <div className="text-sm text-slate-500">Scheduled</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-600">{stats.total_attendance}</div>
              <div className="text-sm text-slate-500">Total Attendance</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Templates */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Quick Start Templates</CardTitle>
          <CardDescription>Create a meeting from a pre-configured template</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(MEETING_TYPE_CONFIG).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <Button
                  key={type}
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => handleCreateFromTemplate(type)}
                >
                  <div className={`p-2 rounded-lg ${config.color} text-white`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm">{config.label}</span>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Meetings List */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Meetings</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                </div>
              ) : meetings.length === 0 ? (
                <div className="text-center py-12">
                  <Video className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-600">No meetings yet</h3>
                  <p className="text-slate-400 mt-1">Create your first safety meeting</p>
                  <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Meeting
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {meetings.map((meeting) => (
                    <MeetingCard key={meeting.id} meeting={meeting} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Meeting Details */}
        <div>
          {selectedMeeting ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Meeting Details</CardTitle>
                  <Badge className={STATUS_CONFIG[selectedMeeting.status]?.color}>
                    {STATUS_CONFIG[selectedMeeting.status]?.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedMeeting.title}</h3>
                  <p className="text-sm text-slate-500">{selectedMeeting.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-slate-500">Date</Label>
                    <div className="font-medium">
                      {new Date(selectedMeeting.scheduled_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-500">Time</Label>
                    <div className="font-medium">{selectedMeeting.scheduled_time}</div>
                  </div>
                  <div>
                    <Label className="text-slate-500">Duration</Label>
                    <div className="font-medium">{selectedMeeting.duration_minutes} min</div>
                  </div>
                  <div>
                    <Label className="text-slate-500">Attendees</Label>
                    <div className="font-medium">{selectedMeeting.attendees?.length || 0}</div>
                  </div>
                </div>
                
                {/* Meeting Link */}
                <div className="p-3 bg-slate-100 rounded-lg">
                  <Label className="text-slate-500 text-xs">Meeting Link</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input 
                      value={selectedMeeting.jitsi_room_url} 
                      readOnly 
                      className="text-xs bg-white"
                    />
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyMeetingLink(selectedMeeting.jitsi_room_url)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="space-y-2">
                  {selectedMeeting.status === 'scheduled' && (
                    <Button className="w-full" onClick={() => handleStartMeeting(selectedMeeting)}>
                      <Play className="w-4 h-4 mr-2" />
                      Start Meeting
                    </Button>
                  )}
                  {selectedMeeting.status === 'in_progress' && (
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setChatMessages(selectedMeeting.chat_messages || []);
                        setShowMeetingRoom(true);
                      }}
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Join Meeting
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(selectedMeeting.jitsi_room_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in New Tab
                  </Button>
                </div>
                
                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-slate-700">Documents</Label>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddDocDialog(true)}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                  {selectedMeeting.documents?.length > 0 ? (
                    <div className="space-y-1">
                      {selectedMeeting.documents.map((doc, idx) => (
                        <a 
                          key={idx}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-slate-50 rounded hover:bg-slate-100 text-sm"
                        >
                          <File className="w-4 h-4 text-blue-500" />
                          {doc.name}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">No documents</p>
                  )}
                </div>
                
                {/* Action Items */}
                <div>
                  <Label className="text-slate-700">Action Items</Label>
                  {selectedMeeting.action_items?.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {selectedMeeting.action_items.map((item, idx) => (
                        <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                          <div className="font-medium">{item.title}</div>
                          <div className="text-slate-500 text-xs">
                            {item.assignee && `Assigned to: ${item.assignee}`}
                            {item.due_date && ` • Due: ${item.due_date}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 mt-1">No action items</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Video className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Select a meeting to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Meeting Room Modal */}
      {showMeetingRoom && <MeetingRoom />}

      {/* Create Meeting Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Safety Meeting</DialogTitle>
            <DialogDescription>Schedule a new safety meeting with video conferencing</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Meeting Title *</Label>
              <Input
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                placeholder="e.g., Weekly Safety Toolbox Talk"
              />
            </div>
            
            <div>
              <Label>Meeting Type</Label>
              <Select 
                value={newMeeting.meeting_type} 
                onValueChange={(v) => setNewMeeting({ ...newMeeting, meeting_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MEETING_TYPE_CONFIG).map(([type, config]) => (
                    <SelectItem key={type} value={type}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newMeeting.description}
                onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                placeholder="Meeting objectives and topics..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newMeeting.scheduled_date}
                  onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={newMeeting.scheduled_time}
                  onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_time: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Duration (minutes)</Label>
              <Select 
                value={String(newMeeting.duration_minutes)} 
                onValueChange={(v) => setNewMeeting({ ...newMeeting, duration_minutes: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateMeeting}>Create Meeting</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Attendee Dialog */}
      <Dialog open={showAddAttendeeDialog} onOpenChange={setShowAddAttendeeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Attendee</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newAttendee.name}
                onChange={(e) => setNewAttendee({ ...newAttendee, name: e.target.value })}
                placeholder="Attendee name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newAttendee.email}
                onChange={(e) => setNewAttendee({ ...newAttendee, email: e.target.value })}
                placeholder="email@company.com"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Input
                value={newAttendee.role}
                onChange={(e) => setNewAttendee({ ...newAttendee, role: e.target.value })}
                placeholder="e.g., Safety Manager"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAttendeeDialog(false)}>Cancel</Button>
            <Button onClick={handleAddAttendee}>Add Attendee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={showAddDocDialog} onOpenChange={setShowAddDocDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Document Name *</Label>
              <Input
                value={newDoc.name}
                onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                placeholder="Document name"
              />
            </div>
            <div>
              <Label>URL *</Label>
              <Input
                value={newDoc.url}
                onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select 
                value={newDoc.type} 
                onValueChange={(v) => setNewDoc({ ...newDoc, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">Link</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="presentation">Presentation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDocDialog(false)}>Cancel</Button>
            <Button onClick={handleAddDocument}>Add Document</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyMeetingsPage;
